import React, { useCallback, useEffect, useMemo, useState } from "react";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { ToastContainer, toast } from "react-toastify";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import EmptyData from "components/EmptyData";
import apiClient from "Utils/apiClient";

const PREMIUM_USERS_ENDPOINT = "/premium-users";
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const TABLE_COLUMNS = [
  { label: "SR", width: 60, sticky: true, left: 0 },
  { label: "User ID", width: 190, sticky: true, left: 60 },
  { label: "Vehicle ID", width: 220, sticky: true, left: 250 },
  { label: "Plan", width: 190 },
  { label: "Plan Type", width: 120 },
  { label: "Subscription Type", width: 150 },
  { label: "Amount Paid", width: 130 },
  { label: "Payment Status", width: 150 },
  { label: "Status", width: 130 },
  { label: "Start Date", width: 165 },
  { label: "End Date", width: 165 },
  { label: "Reference", width: 240 },
];

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);

  return `₦${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getNestedValue = (object, paths, fallback = "—") => {
  for (let i = 0; i < paths.length; i += 1) {
    const keys = paths[i].split(".");
    let current = object;

    for (let j = 0; j < keys.length; j += 1) {
      current = current?.[keys[j]];
      if (current === undefined || current === null || current === "") break;
    }

    if (current !== undefined && current !== null && current !== "") {
      return current;
    }
  }

  return fallback;
};

const getStatusLabel = (row) => {
  const explicitStatus = getNestedValue(row, ["status", "subscriptionStatus", "planStatus"], "");
  if (explicitStatus) return String(explicitStatus);
  if (row?.isCancelled) return "Cancelled";
  if (row?.isActive === false) return "Inactive";
  return "Active";
};

const getStatusChip = (status) => {
  const normalized = String(status || "").toUpperCase();

  if (normalized === "ACTIVE" || normalized === "PAID") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 10px",
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
          backgroundColor: "#e8f5e9",
          color: "#2e7d32",
          whiteSpace: "nowrap",
        }}
      >
        <CheckCircleIcon style={{ fontSize: 14, color: "#2e7d32" }} />
        {normalized === "PAID" ? "Paid" : "Active"}
      </span>
    );
  }

  if (normalized === "CANCELLED" || normalized === "INACTIVE" || normalized === "FAILED") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 10px",
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
          backgroundColor: "#fdecea",
          color: "#c62828",
          whiteSpace: "nowrap",
        }}
      >
        <CancelIcon style={{ fontSize: 14, color: "#c62828" }} />
        {normalized === "FAILED" ? "Failed" : normalized === "CANCELLED" ? "Cancelled" : "Inactive"}
      </span>
    );
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "5px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        backgroundColor: "#eef2f7",
        color: "#4b5563",
        whiteSpace: "nowrap",
      }}
    >
      {status || "Unknown"}
    </span>
  );
};

const renderTruncatedValue = (value, maxWidth) => (
  <span
    title={String(value || "—")}
    style={{
      display: "inline-block",
      maxWidth,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      verticalAlign: "middle",
      cursor: "help",
    }}
  >
    {value || "—"}
  </span>
);

const statCardStyle = {
  backgroundColor: "#fff",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
  border: "1px solid #eef3f8",
  display: "flex",
  alignItems: "center",
  gap: 14,
};

export default function PlanUsers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchPlanUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(PREMIUM_USERS_ENDPOINT);
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      setRows(list);
    } catch (error) {
      toast.error(error?.message || "Failed to fetch plan users");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlanUsers();
  }, [fetchPlanUsers]);

  const normalizedRows = useMemo(() => {
    return rows.map((row, index) => {
      const userId = getNestedValue(row, ["userId._id", "user._id", "userId", "user.id"], "—");
      const vehicleId = getNestedValue(row, ["vehicleId._id", "vehicle._id", "vehicleId"], "—");
      const planName = getNestedValue(row, ["planId.name", "plan.name", "planName", "planId"], "—");
      const planType = getNestedValue(row, ["planId.planType", "plan.planType", "planType"], "—");
      const subscriptionType = getNestedValue(
        row,
        ["planId.subscriptionType", "plan.subscriptionType", "subscriptionType"],
        "—"
      );
      const amountPaid = getNestedValue(
        row,
        ["amountPaid", "amount", "price", "planId.price", "plan.price"],
        ""
      );
      const status = getStatusLabel(row);
      const paymentStatus = getNestedValue(row, ["paymentStatus"], "—");
      const paymentReference = getNestedValue(row, ["paymentReference"], "—");

      return {
        id: row?._id || row?.id || `${userId}-${index}`,
        srNo: String(index + 1).padStart(2, "0"),
        userId,
        vehicleId,
        planName,
        planType,
        subscriptionType,
        amountPaid: formatCurrency(amountPaid),
        paymentStatus,
        paymentReference,
        status,
        startDate: formatDate(
          getNestedValue(row, ["startDate", "subscriptionStartDate", "createdAt", "subscribedAt"], "")
        ),
        endDate: formatDate(
          getNestedValue(row, ["endDate", "expiryDate", "expiresAt", "subscriptionEndDate"], "")
        ),
      };
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return normalizedRows.filter((row) => {
      const matchesSearch =
        !query ||
        String(row.userId).toLowerCase().includes(query) ||
        String(row.vehicleId).toLowerCase().includes(query) ||
        row.planName.toLowerCase().includes(query) ||
        row.planType.toLowerCase().includes(query) ||
        row.subscriptionType.toLowerCase().includes(query) ||
        row.paymentStatus.toLowerCase().includes(query) ||
        row.paymentReference.toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === "All" || row.status.toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [normalizedRows, searchText, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = filteredRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const stats = useMemo(() => {
    const total = normalizedRows.length;
    const paidCount = normalizedRows.filter(
      (row) => String(row.paymentStatus).toUpperCase() === "PAID"
    ).length;
    const activeCount = normalizedRows.filter(
      (row) => String(row.status).toUpperCase() === "ACTIVE"
    ).length;
    const cancelledCount = normalizedRows.filter((row) =>
      ["CANCELLED", "INACTIVE"].includes(String(row.status).toUpperCase())
    ).length;
    const uniquePlans = new Set(
      normalizedRows.map((row) => row.planName).filter((plan) => plan !== "—")
    ).size;

    return [
      {
        title: "Total Plan Users",
        value: total,
        icon: <PeopleAltIcon style={{ fontSize: 26, color: "#1565c0" }} />,
        bg: "#e3f2fd",
      },
      {
        title: "Paid Users",
        value: paidCount,
        icon: <ReceiptLongIcon style={{ fontSize: 26, color: "#2e7d32" }} />,
        bg: "#e8f5e9",
      },
      {
        title: "Active Subscriptions",
        value: activeCount,
        icon: <WorkspacePremiumIcon style={{ fontSize: 26, color: "#6a1b9a" }} />,
        bg: "#f3e5f5",
      },
      {
        title: "Cancelled / Inactive",
        value: cancelledCount,
        icon: <CancelIcon style={{ fontSize: 26, color: "#c62828" }} />,
        bg: "#fdecea",
      },
      {
        title: "Subscribed Plans",
        value: uniquePlans,
        icon: <CheckCircleIcon style={{ fontSize: 26, color: "#b26a00" }} />,
        bg: "#fff3cd",
      },
    ];
  }, [normalizedRows]);

  const headlineStats = useMemo(
    () => ({
      total: normalizedRows.length,
      active: normalizedRows.filter((row) => String(row.status).toUpperCase() === "ACTIVE").length,
      paid: normalizedRows.filter((row) => String(row.paymentStatus).toUpperCase() === "PAID").length,
    }),
    [normalizedRows]
  );

  useEffect(() => {
    setPage(1);
  }, [searchText, statusFilter, rowsPerPage]);

  return (
    <DashboardLayout>
      <div style={{ paddingTop: 64, paddingBottom: 24 }}>
        <ToastContainer position="top-right" autoClose={2500} />

        <div
          style={{
            background: "linear-gradient(135deg, #1565c0 0%, #1976d2 58%, #42a5f5 100%)",
            borderRadius: 18,
            padding: "24px 28px",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
            boxShadow: "0 14px 36px rgba(21, 101, 192, 0.22)",
            marginBottom: 16,
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>Plan Users</h1>
            <p style={{ margin: "6px 0 0", fontSize: 13, opacity: 0.9 }}>
              Monitor premium subscriptions, payments and active plan usage from one dashboard.
            </p>
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {[
              { label: "TOTAL USERS", value: headlineStats.total },
              { label: "ACTIVE", value: headlineStats.active },
              { label: "PAID", value: headlineStats.paid },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  backgroundColor: "rgba(255,255,255,0.16)",
                  borderRadius: 12,
                  padding: "12px 16px",
                  minWidth: 118,
                }}
              >
                <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{item.value}</div>
                <div
                  style={{
                    fontSize: 10,
                    opacity: 0.88,
                    marginTop: 4,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 14,
            marginBottom: 18,
          }}
        >
          {stats.map((item) => (
            <div key={item.title} style={statCardStyle}>
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 14,
                  backgroundColor: item.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, color: "#1a2035", lineHeight: 1 }}>
                  {item.value}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#6b7280",
                    letterSpacing: 0.55,
                    textTransform: "uppercase",
                    marginTop: 5,
                  }}
                >
                  {item.title}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: 18,
            border: "1px solid #edf2f7",
            boxShadow: "0 14px 36px rgba(15, 23, 42, 0.08)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "20px 24px",
              borderBottom: "1px solid #edf2f7",
              background: "linear-gradient(180deg, #ffffff 0%, #f9fbff 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1a2035" }}>
                Premium Users Overview
              </h2>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: "#6b7280" }}>
                Search, filter and review subscription activity with cleaner table navigation.
              </p>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <span
                style={{
                  padding: "6px 12px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  backgroundColor: "#e3f2fd",
                  color: "#1565c0",
                }}
              >
                {filteredRows.length} records
              </span>
              <span
                style={{
                  padding: "6px 12px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  backgroundColor: "#f3e5f5",
                  color: "#6a1b9a",
                }}
              >
                {new Set(filteredRows.map((row) => row.planName)).size} plans
              </span>
            </div>
          </div>

          <div style={{ padding: 24 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 14,
                flexWrap: "wrap",
                marginBottom: 20,
              }}
            >
              <input
                type="text"
                placeholder="Search by user id, vehicle id, plan, type or reference"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                style={{
                  width: "100%",
                  maxWidth: 390,
                  padding: "10px 12px",
                  fontSize: 13,
                  border: "1px solid #d7e0ea",
                  borderRadius: 12,
                  outline: "none",
                  backgroundColor: "#f8fbff",
                  color: "#334155",
                }}
              />

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  style={{
                    minWidth: 160,
                    padding: "10px 12px",
                    fontSize: 13,
                    border: "1px solid #d7e0ea",
                    borderRadius: 12,
                    outline: "none",
                    backgroundColor: "#f8fbff",
                    color: "#334155",
                  }}
                >
                  <option value="All">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Inactive">Inactive</option>
                </select>

                <select
                  value={rowsPerPage}
                  onChange={(event) => setRowsPerPage(Number(event.target.value))}
                  style={{
                    minWidth: 136,
                    padding: "10px 12px",
                    fontSize: 13,
                    border: "1px solid #d7e0ea",
                    borderRadius: 12,
                    outline: "none",
                    backgroundColor: "#f8fbff",
                    color: "#334155",
                  }}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option} / page
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {loading ? (
              <div
                style={{
                  padding: "56px 0",
                  textAlign: "center",
                  color: "#6b7280",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                Loading plan users...
              </div>
            ) : paginatedRows.length === 0 ? (
              <EmptyData title="No plan users found" />
            ) : (
              <>
                <div
                  style={{
                    width: "100%",
                    overflowX: "auto",
                    borderRadius: 16,
                    border: "1px solid #edf2f7",
                    backgroundColor: "#fff",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      minWidth: 1560,
                      borderCollapse: "collapse",
                      tableLayout: "fixed",
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#f8fbff" }}>
                        {TABLE_COLUMNS.map((column) => (
                          <th
                            key={column.label}
                            style={{
                              width: column.width,
                              minWidth: column.width,
                              padding: "12px 14px",
                              borderBottom: "1px solid #edf2f7",
                              fontSize: 11,
                              fontWeight: 800,
                              color: "#6b7280",
                              letterSpacing: 0.55,
                              textTransform: "uppercase",
                              whiteSpace: "nowrap",
                              textAlign: "left",
                              position: column.sticky ? "sticky" : "static",
                              left: column.sticky ? column.left : "auto",
                              zIndex: column.sticky ? 4 : 1,
                              backgroundColor: "#f8fbff",
                            }}
                          >
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRows.map((row, index) => (
                        <tr
                          key={row.id}
                          style={{
                            backgroundColor: index % 2 === 0 ? "#fff" : "#fcfdff",
                          }}
                        >
                          <td
                            style={{
                              width: 60,
                              minWidth: 60,
                              padding: "14px",
                              borderBottom: "1px solid #edf2f7",
                              fontSize: 13,
                              fontWeight: 700,
                              position: "sticky",
                              left: 0,
                              zIndex: 3,
                              backgroundColor: index % 2 === 0 ? "#fff" : "#fcfdff",
                            }}
                          >
                            {row.srNo}
                          </td>
                          <td
                            style={{
                              width: 190,
                              minWidth: 190,
                              padding: "14px",
                              borderBottom: "1px solid #edf2f7",
                              fontSize: 12,
                              fontFamily: "monospace",
                              position: "sticky",
                              left: 60,
                              zIndex: 3,
                              backgroundColor: index % 2 === 0 ? "#fff" : "#fcfdff",
                            }}
                          >
                            {renderTruncatedValue(row.userId, 170)}
                          </td>
                          <td
                            style={{
                              width: 220,
                              minWidth: 220,
                              padding: "14px",
                              borderBottom: "1px solid #edf2f7",
                              fontSize: 12,
                              fontFamily: "monospace",
                              position: "sticky",
                              left: 250,
                              zIndex: 3,
                              backgroundColor: index % 2 === 0 ? "#fff" : "#fcfdff",
                            }}
                          >
                            {renderTruncatedValue(row.vehicleId, 200)}
                          </td>
                          <td style={{ padding: "14px", borderBottom: "1px solid #edf2f7", fontSize: 13, fontWeight: 700, color: "#1a2035" }}>
                            {renderTruncatedValue(row.planName, 170)}
                          </td>
                          <td style={{ padding: "14px", borderBottom: "1px solid #edf2f7", fontSize: 13, color: "#334155" }}>
                            {row.planType}
                          </td>
                          <td style={{ padding: "14px", borderBottom: "1px solid #edf2f7", fontSize: 13, color: "#334155" }}>
                            {row.subscriptionType}
                          </td>
                          <td style={{ padding: "14px", borderBottom: "1px solid #edf2f7", fontSize: 13, fontWeight: 700, color: "#1a2035" }}>
                            {row.amountPaid}
                          </td>
                          <td style={{ padding: "14px", borderBottom: "1px solid #edf2f7" }}>
                            {getStatusChip(row.paymentStatus)}
                          </td>
                          <td style={{ padding: "14px", borderBottom: "1px solid #edf2f7" }}>
                            {getStatusChip(row.status)}
                          </td>
                          <td style={{ padding: "14px", borderBottom: "1px solid #edf2f7", fontSize: 13, color: "#334155" }}>
                            {row.startDate}
                          </td>
                          <td style={{ padding: "14px", borderBottom: "1px solid #edf2f7", fontSize: 13, color: "#334155" }}>
                            {row.endDate}
                          </td>
                          <td style={{ padding: "14px", borderBottom: "1px solid #edf2f7", fontSize: 12, fontFamily: "monospace", color: "#334155" }}>
                            {renderTruncatedValue(row.paymentReference, 220)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div
                  style={{
                    marginTop: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 600 }}>
                    Showing {filteredRows.length ? (currentPage - 1) * rowsPerPage + 1 : 0}-
                    {Math.min(currentPage * rowsPerPage, filteredRows.length)} of {filteredRows.length} users
                  </span>

                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <button
                      type="button"
                      disabled={currentPage === 1}
                      onClick={() => setPage(currentPage - 1)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 10,
                        border: "1px solid #d7e0ea",
                        backgroundColor: "#fff",
                        color: "#475569",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        opacity: currentPage === 1 ? 0.5 : 1,
                      }}
                    >
                      Prev
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((p) => p === 1 || p === totalPages || Math.abs(currentPage - p) <= 1)
                      .map((p, i, arr) => (
                        <React.Fragment key={p}>
                          {i > 0 && arr[i - 1] !== p - 1 && (
                            <span style={{ padding: "0 4px", color: "#94a3b8", fontSize: 12 }}>...</span>
                          )}
                          <button
                            type="button"
                            onClick={() => setPage(p)}
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 10,
                              border: currentPage === p ? "1px solid #1976d2" : "1px solid transparent",
                              backgroundColor: currentPage === p ? "#1976d2" : "#f8fbff",
                              color: currentPage === p ? "#fff" : "#475569",
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            {p}
                          </button>
                        </React.Fragment>
                      ))}

                    <button
                      type="button"
                      disabled={currentPage === totalPages}
                      onClick={() => setPage(currentPage + 1)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 10,
                        border: "1px solid #d7e0ea",
                        backgroundColor: "#fff",
                        color: "#475569",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                        opacity: currentPage === totalPages ? 0.5 : 1,
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
